// fromExisting.js — pontes dos formatos de resultado LEGADOS para o RunOutcome único.
// Consolidação, não reescrita: reusa planRunHonestlyDone + classifyBlocker (a lógica
// honest-green que já existe) e mapeia dagOutcome.reachedGreen. Quando todo consumidor
// ler RunOutcome, as derivações ad-hoc (dagOutcome/sessionStatus) podem sumir. @arch:pure-engine

import { RUN, makeOutcome } from './runOutcome.js';
import { BLOCKER, classifyBlocker } from '../loop/blockerTaxonomy.js';
import { planRunHonestlyDone } from '../loop/planHonestOutcome.js';

// stopReasons que significam "não deu pra VERIFICAR porque o ambiente não roda"
// (worktree sem node_modules etc.) — hoje o loop trata como nudge recuperável; aqui
// vira um estado de 1ª classe, acionável, que PAUSA sem poluir o learning.
const ENV_REASONS = new Set(['no_deps', 'env_broken', 'env_unavailable', 'environment_unavailable', 'deps_missing']);
const isEnvReason = (r) => ENV_REASONS.has(String(r || '')) || /(^|_)(deps|node_modules|env(ironment)?_(missing|broken|unavailable))/.test(String(r || ''));

// Comando de instalação padrão do worktree Node (o estado só nasce por "sem
// node_modules"). Fica na evidence p/ o EnvNoticeCard mostrar/rodar sem adivinhar.
const INSTALL_CMD = 'npm install';

// `root` (cwd da sessão) viaja NA evidence para o env-notice conseguir rodar o
// install no diretório certo — o botão "Instalar" chama run_command com esse cwd.
// Sem root (ex.: chat sem pasta / caminho DAG que não threadou) o card cai p/ "Ver
// comandos" (rodar manual) em vez de um install que erraria.
function envOutcome(reason, root) {
  return makeOutcome({
    state: RUN.ENVIRONMENT_UNAVAILABLE,
    reason,
    detail: 'Verificação indisponível: dependências não instaladas neste worktree.',
    actions: [
      { id: 'install', label: 'Instalar dependências', command: INSTALL_CMD },
      { id: 'cmds', label: 'Ver comandos' },
      { id: 'skip', label: 'Marcar como não-verificável' },
    ],
    evidence: { root: root || null, installCmd: INSTALL_CMD },
  });
}

/**
 * Ponte do run de TURNO ÚNICO (free-form/self-repair). Reusa a MESMA lei que o plano
 * já usa (planRunHonestlyDone) + classifyBlocker pra categorizar por que não entregou.
 * @param {{ finished?: boolean, aborted?: boolean, agentResult?: any, root?: string }} p
 * @returns {import('./runOutcome.js').RunOutcome}
 */
export function fromAgentResult({ finished, aborted, agentResult, root } = {}) {
  const stopReason = agentResult?.stopReason ?? null;
  const reachedGreen = agentResult?.autoCorrect ? agentResult.autoCorrect.reachedGreen : undefined; // true|false|undefined
  const evidence = { model: agentResult?.model, provider: agentResult?.provider };

  if (aborted) return makeOutcome({ state: RUN.CANCELLED, reason: stopReason || 'aborted', evidence });
  // Ambiente antes do gate de entrega: um stopReason de env não está no NON_DONE_STOP do
  // planRunHonestlyDone, então sem isto ele leria como "entregue" (verde silencioso).
  if (isEnvReason(stopReason)) return envOutcome(stopReason, root);

  if (planRunHonestlyDone({ finished, aborted, agentResult })) {
    return reachedGreen === true
      ? makeOutcome({ state: RUN.VERIFIED, verifierRan: true, reason: stopReason, detail: 'Verificado: um verificador executável rodou e passou.', evidence })
      : makeOutcome({ state: RUN.IMPLEMENTED_UNVERIFIED, verifierRan: false, reason: stopReason, detail: 'Entregue — nenhum verificador executável para confirmar (não é verde).', evidence });
  }

  // Não entregou → por quê?
  if (reachedGreen === false) return makeOutcome({ state: RUN.FAILED, verifierRan: true, reason: stopReason, evidence });

  const cat = classifyBlocker({ stopReason, ok: agentResult?.ok, autoCorrect: agentResult?.autoCorrect, blocked: agentResult?.blocked }).type;
  if (cat === BLOCKER.CANCELLED) return makeOutcome({ state: RUN.CANCELLED, reason: stopReason, evidence });
  if (cat === BLOCKER.FAILED) return makeOutcome({ state: RUN.FAILED, reason: stopReason, evidence });
  // MISSING_EVIDENCE = unbacked_green_claim (alegou verde sem verificador). NÃO é
  // "implementado-sem-verificador" (esse é o ramo planRunHonestlyDone=true acima, com
  // mudanças no disco e fim honesto): aqui o run NÃO entregou honestamente, então NUNCA
  // pode CONCLUIR — vira BLOCKED (cobra evidência, pausa) pra não furar a lei honest-green.
  // CONTINUE / ASK_USER / EXTERNAL_WAIT também são recuperáveis → pausa (blocked).
  return makeOutcome({ state: RUN.BLOCKED, reason: stopReason, evidence });
}

/**
 * Ponte do resultado do DAG (dagOutcome.mapDagResultToOutcome → {reachedGreen, stopReason, …}).
 * @param {{ reachedGreen: boolean|null, stopReason?: string, tasksFailed?: number, root?: string }} o
 * @returns {import('./runOutcome.js').RunOutcome}
 */
export function fromDagOutcome(o) {
  const reason = o?.stopReason ?? null;
  if (o?.reachedGreen === true) return makeOutcome({ state: RUN.VERIFIED, verifierRan: true, reason, evidence: { surface: 'dag' } });
  if (o?.reachedGreen === false) return makeOutcome({ state: RUN.FAILED, verifierRan: true, reason, evidence: { surface: 'dag' } });
  // reachedGreen === null → all-done-sem-verificador vs blocked/unreachable (o stopReason desempata)
  if (reason === 'dag_done') return makeOutcome({ state: RUN.IMPLEMENTED_UNVERIFIED, reason, evidence: { surface: 'dag' } });
  if (isEnvReason(reason)) return envOutcome(reason, o?.root);
  return makeOutcome({ state: RUN.BLOCKED, reason, evidence: { surface: 'dag' } });
}
